import { useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ITemplateAssignmentDialogProps {
  // LOCAL_TYPE_JUSTIFIED: component-local prop interface, no domain reuse
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: Id<'policyTemplates'>;
  templateName: string;
}

export function TemplateAssignmentDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
}: ITemplateAssignmentDialogProps) {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const orgs = useQuery(api.superadmin.queries.listOrgsForFilter);
  const assignTemplate = useMutation(api.superadmin.mutations.assignTemplateToOrg);

  const handleAssign = useCallback(async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organisation');
      return;
    }
    try {
      await assignTemplate({
        templateId,
        orgId: selectedOrgId as Id<'organisations'>, // AS_JUSTIFIED: one-time Id cast from select value
      });
      toast.success('Template assigned');
      setSelectedOrgId('');
      onOpenChange(false);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to assign template';
      toast.error(msg);
    }
  }, [selectedOrgId, templateId, assignTemplate, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign "{templateName}" to Organisation</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
            <SelectTrigger>
              <SelectValue placeholder="Select organisation..." />
            </SelectTrigger>
            <SelectContent>
              {(orgs ?? []).map(org => (
                <SelectItem key={org._id} value={org._id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedOrgId}>
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
